// Shared report rendering component — pure leaf, no API calls. Safe to reuse
// in both authenticated `/reports/{id}` and public `/share/report/{token}` views.
import React, { useState } from "react";

function num(n, d = 2) {
  if (n === null || n === undefined) return "—";
  const v = Number(n);
  if (!isFinite(v)) return "—";
  return v.toFixed(d);
}

function formatCost(c) {
  if (!c) return "$0.00";
  return c < 0.01 ? `$${c.toFixed(4)}` : `$${c.toFixed(3)}`;
}

// Minimal markdown rendering — headings, bold, italics, inline code, tables, [#N] citations.
function renderMarkdown(md) {
  if (!md) return null;
  const lines = md.split("\n");
  const out = [];
  let i = 0;
  let key = 0;

  const inline = (text) => {
    // citation pills: [#3] -> styled span
    const parts = [];
    let lastIdx = 0;
    const re = /\[#?(\d+)\]/g;
    let m;
    while ((m = re.exec(text)) !== null) {
      if (m.index > lastIdx) parts.push(text.slice(lastIdx, m.index));
      parts.push(
        <a key={`c-${key++}`} href={`#claim-${m[1]}`}
           className="inline-block text-[10px] font-mono px-1 py-px rounded
                      bg-emerald-100 text-emerald-800 hover:bg-emerald-200 align-middle ml-0.5"
           data-testid={`narrative-citation-${m[1]}`}>#{m[1]}</a>
      );
      lastIdx = m.index + m[0].length;
    }
    if (lastIdx < text.length) parts.push(text.slice(lastIdx));
    // Phase 6 polish — convert [[MM:tooltip::token]] sentinels into <mark> with tooltip
    const withMarks = parts.flatMap((p, idx) => {
      if (typeof p !== "string") return [p];
      const out = [];
      const mre = /\[\[MM:([^:]+)::([^\]]+)\]\]/g;
      let li2 = 0;
      let mm;
      while ((mm = mre.exec(p)) !== null) {
        if (mm.index > li2) out.push(p.slice(li2, mm.index));
        const tip = decodeURIComponent(mm[1]);
        const tok = mm[2];
        out.push(
          <mark key={`mm-${idx}-${li2}`}
                className="bg-amber-200 text-amber-900 px-0.5 rounded cursor-help"
                title={tip}
                data-testid="report-mismatch-highlight"
                data-mismatch-tooltip={tip}>
            <span data-testid="report-mismatch-tooltip" className="sr-only">{tip}</span>
            {tok}
          </mark>
        );
        li2 = mm.index + mm[0].length;
      }
      if (li2 < p.length) out.push(p.slice(li2));
      return out;
    });
    // **bold** rendering
    return withMarks.flatMap((p, idx) => {
      if (typeof p !== "string") return [p];
      const bits = [];
      const br = /\*\*([^*]+)\*\*/g;
      let li = 0;
      let bm;
      while ((bm = br.exec(p)) !== null) {
        if (bm.index > li) bits.push(p.slice(li, bm.index));
        bits.push(<strong key={`b-${idx}-${li}`}>{bm[1]}</strong>);
        li = bm.index + bm[0].length;
      }
      if (li < p.length) bits.push(p.slice(li));
      return bits;
    });
  };

  while (i < lines.length) {
    const line = lines[i];
    // table block
    if (line.startsWith("|") && i + 1 < lines.length && /^\|[-\s|:]+\|/.test(lines[i + 1])) {
      const headers = line.split("|").slice(1, -1).map((c) => c.trim());
      i += 2;
      const rows = [];
      while (i < lines.length && lines[i].startsWith("|")) {
        rows.push(lines[i].split("|").slice(1, -1).map((c) => c.trim()));
        i++;
      }
      out.push(
        <div key={key++} className="my-4 overflow-x-auto">
          <table className="text-xs font-mono w-full">
            <thead>
              <tr className="border-b border-zinc-300 bg-zinc-50">
                {headers.map((h, hi) => (
                  <th key={hi} className="text-left px-3 py-2 font-semibold uppercase text-[10px] tracking-wider text-zinc-700">{inline(h)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((r, ri) => (
                <tr key={ri} className="border-b border-zinc-100">
                  {r.map((c, ci) => (
                    <td key={ci} className="px-3 py-2 text-zinc-800">{inline(c)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
      continue;
    }
    if (line.startsWith("## ")) {
      out.push(<h2 key={key++} className="text-base font-semibold text-zinc-900 mt-6 mb-2 tracking-tight">{inline(line.slice(3))}</h2>);
    } else if (line.startsWith("# ")) {
      out.push(<h1 key={key++} className="text-lg font-semibold text-zinc-900 mt-6 mb-2 tracking-tight">{inline(line.slice(2))}</h1>);
    } else if (line.trim() === "") {
      out.push(<div key={key++} className="h-2" />);
    } else if (line.startsWith("- ")) {
      // collect list items
      const items = [];
      while (i < lines.length && lines[i].startsWith("- ")) {
        items.push(lines[i].slice(2));
        i++;
      }
      out.push(
        <ul key={key++} className="list-disc pl-6 my-2 text-sm text-zinc-800 space-y-1">
          {items.map((it, ii) => <li key={ii}>{inline(it)}</li>)}
        </ul>
      );
      continue;
    } else {
      out.push(<p key={key++} className="text-sm text-zinc-800 leading-relaxed my-2">{inline(line)}</p>);
    }
    i++;
  }
  return <div>{out}</div>;
}

export default function ReportView({ report }) {
  const [openCite, setOpenCite] = useState(null);
  if (!report) return null;
  const validation = report.validation || {};
  const llmCalls = report.llm_calls || [];
  const citations = report.citations || [];
  const mismatches = validation.mismatches || [];
  const totalCost = llmCalls.reduce((a, c) => a + (c.cost_estimate_usd || 0), 0);
  const totalTokens = llmCalls.reduce((a, c) => a + (c.prompt_tokens || 0) + (c.completion_tokens || 0), 0);
  const totalLatency = llmCalls.reduce((a, c) => a + (c.latency_ms || 0), 0);

  // Phase 6 Path A polish — pre-process narrative to wrap each mismatched token
  // in a <mark> with the side-by-side humanized values in title (tooltip).
  // Done once via a marker string the markdown renderer recognises.
  let narrative = report.narrative || "";
  if (mismatches.length) {
    for (const m of mismatches) {
      if (!m.token) continue;
      const tooltip = `Narrative said ${m.narrative_humanized || m.token}; source has ${m.closest_data_humanized || "—"} (claim #${m.claim_id})`;
      // Use a sentinel form that survives markdown rendering — we handle it in `inline`.
      const sentinel = `[[MM:${encodeURIComponent(tooltip)}::${m.token}]]`;
      // Replace only the FIRST occurrence so duplicate tokens still render once.
      narrative = narrative.replace(m.token, sentinel);
    }
  }

  return (
    <div className="space-y-6">
      {/* Question */}
      <div data-testid="report-question" className="border border-zinc-200 rounded-lg p-4 bg-zinc-50">
        <div className="text-[10px] uppercase font-mono text-zinc-500 mb-1">Question</div>
        <div className="text-sm text-zinc-900 font-medium">{report.question}</div>
        <div className="mt-2 text-[11px] font-mono text-zinc-500">
          report_id: <span className="text-zinc-700">{report.report_id}</span> · created {new Date(report.created_at).toLocaleString()}
          {report.context_ccn ? <> · ccn <span className="text-zinc-700">{report.context_ccn}</span></> : null}
          {report.context_ein ? <> · ein <span className="text-zinc-700">{report.context_ein}</span></> : null}
        </div>
      </div>

      {/* Validation badge */}
      <div data-testid="report-validation-badge"
           className={`border rounded-lg p-3 flex items-start gap-3 ${
             validation.passed
               ? "border-emerald-300 bg-emerald-50"
               : "border-amber-400 bg-amber-50"
           }`}>
        <div className={`text-[10px] uppercase font-mono px-2 py-0.5 rounded ${
          validation.passed ? "bg-emerald-700 text-white" : "bg-amber-600 text-white"
        }`}>
          {validation.passed ? "All claims verified" : "Validation warnings"}
        </div>
        <div className="text-xs text-zinc-700 flex-1">
          <div>
            {validation.citation_count || 0} citations · {(validation.unique_citations || []).length} unique sources
            {!validation.passed && mismatches.length > 0 && (
              <span className="ml-2 text-amber-900">
                · {mismatches.length} unverified claim{mismatches.length === 1 ? "" : "s"} — highlighted in narrative
              </span>
            )}
          </div>
          {!validation.passed && (
            <ul className="mt-1 list-disc pl-5 space-y-0.5 text-amber-900" data-testid="report-validation-errors">
              {(validation.errors || []).map((e, i) => <li key={i} className="font-mono text-[11px]">{e}</li>)}
            </ul>
          )}
        </div>
      </div>

      {/* Narrative */}
      <div data-testid="report-narrative" className="border border-zinc-200 rounded-lg p-5 bg-white">
        {renderMarkdown(narrative)}
      </div>

      {/* Citations */}
      <div data-testid="report-citations">
        <div className="text-[10px] uppercase font-mono text-zinc-500 mb-2">Citations ({citations.length})</div>
        <div className="space-y-2">
          {citations.map((c) => (
            <div key={c.claim_id} id={`claim-${c.claim_id}`}
                 className="border border-zinc-200 rounded-lg bg-white p-3 text-xs font-mono"
                 data-testid={`citation-${c.claim_id}`}>
              <button
                onClick={() => setOpenCite(openCite === c.claim_id ? null : c.claim_id)}
                className="flex items-center gap-2 w-full text-left text-zinc-700 hover:text-zinc-900">
                <span className="bg-emerald-100 text-emerald-800 px-1.5 py-0.5 rounded">#{c.claim_id}</span>
                <span className="font-semibold">{c.source?.endpoint}</span>
                <span className="text-zinc-500">{JSON.stringify(c.source?.params || {})}</span>
                <span className="ml-auto text-zinc-400">{openCite === c.claim_id ? "▾" : "▸"}</span>
              </button>
              {c.why ? <div className="mt-1 text-[11px] text-zinc-500 normal-case">{c.why}</div> : null}
              {openCite === c.claim_id && (
                <div className="mt-2 border-t border-zinc-100 pt-2 text-[11px] text-zinc-600 break-words">
                  <div>source: <span className="text-zinc-800">{JSON.stringify(c.source?.lineage_ref || {})}</span></div>
                  <div className="mt-1">status: <span className="text-zinc-800">{c.status || "—"}</span></div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Telemetry footer */}
      <div data-testid="report-telemetry-footer"
           className="border border-zinc-200 rounded-lg bg-zinc-900 text-zinc-300 p-3 text-[11px] font-mono">
        <div className="uppercase tracking-wider text-zinc-500 mb-1">Telemetry</div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-1">
          <div>calls: <span className="text-white">{llmCalls.length}</span></div>
          <div>tokens: <span className="text-white">{totalTokens.toLocaleString()}</span></div>
          <div>cost: <span className="text-white">{formatCost(totalCost)}</span></div>
          <div>latency: <span className="text-white">{(totalLatency / 1000).toFixed(2)}s</span></div>
        </div>
        <div className="mt-2 space-y-0.5">
          {llmCalls.map((c, i) => (
            <div key={i} className="text-zinc-400">
              · <span className="text-zinc-200">{c.phase}</span> {c.provider}/{c.model}
              {" "}{c.latency_ms}ms · {c.prompt_tokens || 0}in / {c.completion_tokens || 0}out
              {" "}· {formatCost(c.cost_estimate_usd || 0)}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
